"use client";

import React, { useState, useEffect } from "react";
import { ArrowRight, GitCommitHorizontal as GitCommit, CircleCheck as CheckCircle2, Compass, Award, Sparkles, Layers, BookOpen } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface LegoPathwayConsumerProps {
  lang: "vi" | "en";
}
function parseLines(text?: string): string[] {
  if (!text) return [];
  return text.split(/\r?\n|•|\.\s+(?=[A-Z0-9À-Ỹ])/).map(line => line.trim().replace(/^[-*•\d.)]\s*/, "")).filter(line => line.length > 0);
}
function LegoPathwayConsumer({
  lang
}: LegoPathwayConsumerProps) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const legoProgram = posts.find(p => p.customFields?.program_name?.trim().toLowerCase() === "lego robotics") || posts.find(p => p.title?.toLowerCase().includes("lego robotics") || p.customFields?.program_name?.toLowerCase().includes("lego")) || posts[0];
  const learningJourneyRaw = legoProgram?.customFields?.learning_journey;
  const outcomesRaw = legoProgram?.customFields?.outcomes;
  const programName = legoProgram?.customFields?.program_name || legoProgram?.title || "LEGO Robotics";
  const tagline = legoProgram?.customFields?.tagline;
  const ageRange = legoProgram?.customFields?.age_range;
  const level = legoProgram?.customFields?.level;
  const journeySteps = parseLines(learningJourneyRaw);
  const outcomeItems = parseLines(outcomesRaw);
  const t = {
    vi: {
      eyebrow: "NỘI DUNG & HÀNH TRÌNH HỌC TẬP / CURRICULUM & PATHWAY",
      title: `Nội dung & Lộ trình ${programName}`,
      subtitle: "Tiến trình phát triển tư duy thuật toán, kỹ năng lắp ráp cơ khí và giải quyết vấn đề qua từng giai đoạn bài bản.",
      journeyTitle: "Hành trình học tập (Learning Journey)",
      journeyDesc: "Các bước phát triển năng lực công nghệ và cơ chế tư duy của học viên trong chương trình.",
      outcomesTitle: "Chuẩn đầu ra (Key Learning Outcomes)",
      outcomesDesc: "Những kỹ năng và năng lực cụ thể học viên tự tin làm chủ sau khi hoàn thành khóa học.",
      stepPrefix: "BƯỚC",
      outcomePrefix: "MỤC TIÊU",
      loading: "Đang tải dữ liệu chương trình LEGO Robotics...",
      notFound: "Chưa tìm thấy dữ liệu khóa học LEGO Robotics.",
      fullPathwayBoxTitle: "Chi tiết toàn bộ cấp độ (D1–D4, HP1–HP6, Enjoy AI)",
      fullPathwayBoxDesc: "Thông tin chi tiết từng khóa học, giáo trình từng giai đoạn và chuẩn thi đấu quốc tế được cập nhật đầy đủ tại trang lộ trình chuyên sâu.",
      ctaButton: "Xem lộ trình Robotics đầy đủ"
    },
    en: {
      eyebrow: "CURRICULUM & PATHWAY / NỘI DUNG & HÀNH TRÌNH",
      title: `${programName} Content & Pathway`,
      subtitle: "Structured milestone progression cultivating algorithmic thinking, hardware mechanisms, and problem-solving.",
      journeyTitle: "Learning Journey",
      journeyDesc: "Step-by-step technological competencies and engineering mindset development.",
      outcomesTitle: "Key Learning Outcomes",
      outcomesDesc: "Concrete skills, knowledge, and capabilities mastered by students upon completion.",
      stepPrefix: "STEP",
      outcomePrefix: "OUTCOME",
      loading: "Loading LEGO Robotics curriculum data...",
      notFound: "LEGO Robotics program data not found.",
      fullPathwayBoxTitle: "Full Stage-by-Stage Detail (D1–D4, HP1–HP6, Enjoy AI)",
      fullPathwayBoxDesc: "Explore complete course-by-course syllabi, age progressions, and competition tracks on the dedicated Robotics Pathway page.",
      ctaButton: "View the full Robotics Pathway"
    }
  }[lang];
  if (loading) {
    return <div className="py-12 text-center">
        <div className="inline-flex items-center gap-2 text-sm font-mono text-muted-foreground animate-pulse">
          <Layers className="size-4 animate-spin text-primary" />
          <span>{t.loading}</span>
        </div>
      </div>;
  }
  if (error || !legoProgram) {
    return <div className="py-12 text-center text-sm text-muted-foreground">
        {t.notFound}
      </div>;
  }
  return <div className="space-y-12">
      {/* Program Summary Meta Banner */}
      <div className="rounded-xl bg-[#0b1329] border border-blue-500/30 text-white p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-20 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:20px_20px]" aria-hidden="true" />
        <div className="absolute -top-24 -right-24 size-80 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-mono text-cyan-300 uppercase tracking-widest">
              <Compass className="size-4 text-cyan-400" />
              <span>ROBOTO PROGRAM ARCHITECTURE / {programName}</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold font-serif text-white">
              {programName}
            </h3>
            {tagline && <p className="text-sm text-slate-300 max-w-2xl leading-relaxed">
                {tagline}
              </p>}
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {ageRange && <Badge className="bg-secondary/20 text-secondary-foreground border border-secondary/40 font-mono text-xs px-3 py-1">
                {ageRange}
              </Badge>}
            {level && <Badge className="bg-blue-500/20 text-cyan-300 border border-cyan-400/30 font-mono text-xs px-3 py-1">
                {level}
              </Badge>}
          </div>
        </div>
      </div>

      {/* 2-Column Staged Visual: Learning Journey & Outcomes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
        {/* Card 1: Learning Journey */}
        <Card className="relative flex flex-col justify-between w-full border-border/80 bg-card/95 hover:border-secondary transition-[color,background-color,border-color,box-shadow] duration-300 shadow-sm hover:shadow-md overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/80 to-secondary" />

          <CardHeader className="pb-4 pt-6 px-6 sm:px-8">
            <div className="flex items-center justify-between gap-3 mb-3">
              <Badge variant="secondary" className="font-mono text-xs px-2.5 py-0.5 rounded-sm bg-secondary/15 text-secondary-foreground border border-secondary/30">
                HÀNH TRÌNH / PROGRESSION
              </Badge>
              <span className="font-mono text-[10px] text-muted-foreground/70 uppercase tracking-widest">
                SPEC-01 / JOURNEY
              </span>
            </div>

            <CardTitle className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
              <GitCommit className="size-5 text-primary shrink-0" />
              {t.journeyTitle}
            </CardTitle>

            <CardDescription className="text-xs text-muted-foreground leading-relaxed mt-1">
              {t.journeyDesc}
            </CardDescription>
          </CardHeader>

          <CardContent className="px-6 sm:px-8 flex-1 flex flex-col justify-between space-y-6">
            <div className="p-4 sm:p-5 rounded-lg bg-muted/50 border border-border/60 relative">
              {journeySteps.length > 0 ? <div className="relative space-y-3.5">
                  <div className="absolute left-[19px] top-3 bottom-3 w-0.5 bg-gradient-to-b from-primary via-secondary to-primary/40 opacity-40 pointer-events-none" />

                  {journeySteps.map((step, idx) => <div key={idx} data-index={idx} className="relative flex items-start gap-3 z-10">
                      <div className="size-10 rounded-md bg-background border border-primary/30 flex items-center justify-center text-primary font-mono text-xs font-bold shrink-0 shadow-2xs">
                        {String(idx + 1).padStart(2, "0")}
                      </div>
                      <div className="flex-1 min-w-0 bg-background/90 p-3 rounded-md border border-border/50">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-primary font-semibold block mb-0.5">
                          {t.stepPrefix} 0{idx + 1}
                        </span>
                        <p className="text-xs sm:text-sm text-foreground leading-relaxed">
                          {step}
                        </p>
                      </div>
                    </div>)}
                </div> : <p className="text-xs sm:text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                  {learningJourneyRaw || t.notFound}
                </p>}
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Key Learning Outcomes */}
        <Card className="relative flex flex-col justify-between w-full border-border/80 bg-card/95 hover:border-secondary transition-[color,background-color,border-color,box-shadow] duration-300 shadow-sm hover:shadow-md overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-secondary via-accent to-primary" />

          <CardHeader className="pb-4 pt-6 px-6 sm:px-8">
            <div className="flex items-center justify-between gap-3 mb-3">
              <Badge variant="outline" className="font-mono text-xs px-2.5 py-0.5 rounded-sm bg-accent/10 text-foreground border border-accent/40">
                CHUẨN ĐẦU RA / OUTCOMES
              </Badge>
              <span className="font-mono text-[10px] text-muted-foreground/70 uppercase tracking-widest">
                SPEC-02 / MASTERY
              </span>
            </div>

            <CardTitle className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
              <Award className="size-5 text-secondary shrink-0" />
              {t.outcomesTitle}
            </CardTitle>

            <CardDescription className="text-xs text-muted-foreground leading-relaxed mt-1">
              {t.outcomesDesc}
            </CardDescription>
          </CardHeader>

          <CardContent className="px-6 sm:px-8 flex-1 flex flex-col justify-between space-y-6">
            <div className="p-4 sm:p-5 rounded-lg bg-muted/50 border border-border/60">
              {outcomeItems.length > 0 ? <div className="space-y-3">
                  {outcomeItems.map((item, idx) => <div key={idx} data-index={idx} className="p-3 rounded-md bg-background/90 border border-border/50 flex items-start gap-3">
                      <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-secondary font-semibold block mb-0.5">
                          {t.outcomePrefix} 0{idx + 1}
                        </span>
                        <p className="text-xs sm:text-sm text-foreground leading-relaxed">
                          {item}
                        </p>
                      </div>
                    </div>)}
                </div> : <p className="text-xs sm:text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                  {outcomesRaw || t.notFound}
                </p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dedicated CTA Navigation Panel to RoboticsLearningPathwayPage */}
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-1.5 max-w-2xl">
          <div className="flex items-center gap-2 text-xs font-mono text-primary uppercase font-bold tracking-wider">
            <BookOpen className="size-4 text-primary" />
            <span>ROBOTICS LEARNING PATHWAY DIRECTORY</span>
          </div>
          <h4 className="text-lg sm:text-xl font-bold font-serif text-foreground">
            {t.fullPathwayBoxTitle}
          </h4>
          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            {t.fullPathwayBoxDesc}
          </p>
        </div>

        <Button asChild variant="default" className="h-11 px-6 text-sm font-semibold shadow-xs shrink-0 flex items-center gap-2">
          <Link to="/RoboticsLearningPathwayPage">
            <span>{t.ctaButton}</span>
            <ArrowRight className="size-4" />
          </Link>
        </Button>
      </div>
    </div>;
}
export default function PathwayComparison() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const getSavedLang = () => {
      if (typeof window !== "undefined") {
        const saved = localStorage.getItem("roboto_lang");
        if (saved === "en" || saved === "vi") return saved;
      }
      return "vi";
    };
    setLang(getSavedLang());
    const handleStorageChange = () => {
      const saved = localStorage.getItem("roboto_lang");
      if (saved === "en" || saved === "vi") {
        setLang(saved);
      }
    };
    const handleCustomLangChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: string;
      }>;
      const newLang = customEvent.detail?.lang;
      if (newLang === "en" || newLang === "vi") {
        setLang(newLang);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("roboto-lang-change", handleCustomLangChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("roboto-lang-change", handleCustomLangChange);
    };
  }, []);
  const wp_query = new WP_Query({
    post_type: "program",
    per_page: 20,
    status: ["publish"]
  });
  const headerContent = {
    vi: {
      eyebrow: "CONTENT & PATHWAY / NỘI DUNG & LỘ TRÌNH CHƯƠNG TRÌNH",
      title: "Nội dung & Lộ trình đào tạo LEGO Robotics",
      subtitle: "Tìm hiểu hành trình phát triển toàn diện từ tư duy logic đến cơ chế vận hành robot, chuẩn bị nền tảng công nghệ vững chắc."
    },
    en: {
      eyebrow: "CONTENT & PATHWAY / NỘI DUNG & LỘ TRÌNH CHƯƠNG TRÌNH",
      title: "LEGO Robotics Content & Learning Pathway",
      subtitle: "Explore the comprehensive journey from logic and spatial reasoning to robotic engineering mechanisms."
    }
  }[lang];
  return <section id="pathwayvsspecialized" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden border-b border-border/60">
      {/* Blueprint Grid & Coordinate Background Texture */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.06] bg-[radial-gradient(var(--foreground)_1px,transparent_1px)] [background-size:24px_24px]" aria-hidden="true" />

      {/* Spec lines / ambient glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-32 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -right-20 w-80 h-80 bg-secondary/15 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-mono text-[11px] sm:text-xs tracking-wider uppercase border-primary/30 text-primary bg-primary/5 rounded-md inline-flex items-center gap-1.5">
            <Sparkles className="size-3 text-primary" />
            {headerContent.eyebrow}
          </Badge>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground font-serif">
            {headerContent.title}
          </h2>

          <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
            {headerContent.subtitle}
          </p>
        </div>

        {/* Dynamic CPT Data Provider & Content */}
        <PostsProvider wp_query={{
        post_type: "program",
        per_page: 20,
        status: ["publish"]
      }}>
          <LegoPathwayConsumer lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}